import React, { useRef, useState, useEffect } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { useLocation } from "@gatsbyjs/reach-router"
import { ReactLenis, useLenis } from "@studio-freight/react-lenis"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import PageTransition from "./pageTransition"

import Header from "./header"
import Footer from "./footer"
import { gsap } from "gsap"
import Menu from "./menu"

import "../scss/site.scss"

gsap.registerPlugin(ScrollTrigger)

const LENIS_OPTIONS = { duration: 1.2, easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true }

export default function Layout({ children }) {
    const { site } = useStaticQuery(graphql`
        query SiteTitleQuery {
            site {
                siteMetadata {
                    title
                }
            }
        }
    `)

    const { pathname } = useLocation()
    const darkHeader = pathname.startsWith("/services") || pathname.startsWith("/contact")

    const lenis = useLenis(() => ScrollTrigger.update())

    const mainRef = useRef(null)
    const footerRef = useRef(null)
    const [footerHeight, setFooterHeight] = useState(0)
    const [footerFixed, setFooterFixed] = useState(true)
    const [footerVisible, setFooterVisible] = useState(false)

    useEffect(() => {
        if (lenis) lenis.scrollTo(0, { immediate: true })
        else window.scrollTo(0, 0)
    }, [pathname, lenis])

    useEffect(() => {
        const measure = () => {
            const height = footerRef.current?.offsetHeight || 0
            setFooterHeight(height)
            setFooterFixed(height > 0 && height <= window.innerHeight)
        }
        measure()
        window.addEventListener("resize", measure)
        return () => window.removeEventListener("resize", measure)
    }, [])

    useEffect(() => {
        const onScroll = () => {
            const main = mainRef.current
            const footer = footerRef.current
            if (!main || !footer) return
            const threshold = main.offsetHeight + footer.offsetHeight / 2
            setFooterVisible(window.scrollY + window.innerHeight >= threshold)
        }
        onScroll()
        window.addEventListener("scroll", onScroll, { passive: true })
        window.addEventListener("resize", onScroll)
        return () => {
            window.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", onScroll)
        }
    }, [footerHeight])

    return (
        <ReactLenis root options={LENIS_OPTIONS}>
            <Header darkHeader={darkHeader} />
            <Menu />
            <PageTransition>
                <main ref={mainRef} style={{ marginBottom: footerFixed ? footerHeight : 0 }}>
                    {children}
                </main>
                <Footer ref={footerRef} visible={footerVisible} fixed={footerFixed} />
            </PageTransition>
        </ReactLenis>
    )
}
